import { createEntityAdapter, type EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import {
  type ChatConversation,
  type PendingActionTarget,
  type RunningTemplateConfigurationSetupName,
  type SendChatMessageInput,
  toOptimisticText,
  toRunningTemplateConfigurationSetupName,
} from '@blaze-dashboard/ai-chat-domain';
import { type HttpRequestStatus } from '@blaze-shared/shared-util';

import * as ChatActionTargetActions from './chat-action-target.actions';
import * as ChatConversationActions from './chat-conversation.actions';
import * as ChatFeedbackActions from './chat-feedback.actions';
import * as ChatInterruptActions from './chat-interrupt.actions';
import * as ChatMessagesActions from './chat-messages.actions';
import * as ChatPollingActions from './chat-polling.actions';
import * as ChatTemplateConfigurationSetupActions from './chat-template-configuration-setup.actions';
import { type ChatMessage, type SentFeedback } from './chat.models';

export const AI_CHAT_FEATURE_KEY = 'ai-chat';

export interface State extends EntityState<ChatMessage> {
  conversation: {
    status: HttpRequestStatus;
    error?: string;
    conversation?: ChatConversation;
    isRunning: boolean;
    contextUsage: number;
  };
  polling: {
    status: HttpRequestStatus;
    error?: string;
    chatConversationId?: string;
  };
  attachedConversationId?: string;
  panelProjectId?: string;
  hasLoadedMessages: boolean;
  sendError?: string;
  lastFailedMessage?: SendChatMessageInput;
  pendingActionTarget?: PendingActionTarget;
  feedback: SentFeedback;
  interruptError?: string;
  runningTemplateConfigurationSetup?: RunningTemplateConfigurationSetupName;
  awaitingTemplateConfigurationSetupOutcome: boolean;
  awaitingNewConversationBoundary: boolean;
  newConversationBoundaryMessageIds: string[];
}

export const entityAdapter = createEntityAdapter<ChatMessage>({
  selectId: (m) => m.id,
});

export const initialState: State = entityAdapter.getInitialState({
  conversation: {
    status: 'none',
    isRunning: false,
    contextUsage: 0,
  },
  polling: {
    status: 'none',
  },
  feedback: {},
  hasLoadedMessages: false,
  awaitingTemplateConfigurationSetupOutcome: false,
  awaitingNewConversationBoundary: false,
  newConversationBoundaryMessageIds: [],
});

function establishedConversation(
  conversation: ChatConversation | undefined,
): State['conversation'] {
  return {
    status: 'success',
    conversation,
    error: undefined,
    isRunning: false,
    contextUsage: 0,
  };
}

function attachToConversation(state: State, conversation: ChatConversation): State {
  return entityAdapter.removeAll({
    ...state,
    conversation: establishedConversation(conversation),
    attachedConversationId: conversation.id,
    sendError: undefined,
    lastFailedMessage: undefined,
    pendingActionTarget: undefined,
    feedback: {},
    interruptError: undefined,
    runningTemplateConfigurationSetup: undefined,
    awaitingTemplateConfigurationSetupOutcome: false,
    hasLoadedMessages: false,
    awaitingNewConversationBoundary: false,
    newConversationBoundaryMessageIds: [],
  });
}

function answersAttachedConversation(
  state: State,
  chatConversationId: string | undefined,
): boolean {
  return !!chatConversationId && state.attachedConversationId === chatConversationId;
}

function putsRightThreadOnScreen(state: State, chatConversationId: string | undefined): boolean {
  return !chatConversationId || answersAttachedConversation(state, chatConversationId);
}

function reportedTemplateConfigurationSetup(
  state: State,
  wizardName: string | null | undefined,
): RunningTemplateConfigurationSetupName | undefined {
  return state.awaitingTemplateConfigurationSetupOutcome
    ? state.runningTemplateConfigurationSetup
    : toRunningTemplateConfigurationSetupName(wizardName);
}

function findNewConversationBoundary(
  state: State,
  messages: readonly ChatMessage[],
): ChatMessage | undefined {
  if (!state.awaitingNewConversationBoundary) {
    return undefined;
  }
  return messages.find((message) => !state.entities[message.id]);
}

export const reducer = createReducer(
  initialState,
  on(
    ChatConversationActions.loadLatestChatConversation,
    ChatConversationActions.createChatConversation,
    (state, { projectId }): State => ({
      ...state,
      panelProjectId: projectId,
      conversation: {
        status: 'pending',
        isRunning: false,
        contextUsage: 0,
      },
    }),
  ),
  on(
    ChatConversationActions.loadLatestChatConversationSuccess,
    (state, { conversation }): State => {
      if (conversation.id !== state.attachedConversationId) {
        return attachToConversation(state, conversation);
      }
      return { ...state, conversation: establishedConversation(conversation) };
    },
  ),
  on(
    ChatConversationActions.createChatConversationSuccess,
    (state, { conversation }): State => attachToConversation(state, conversation),
  ),
  on(
    ChatConversationActions.loadLatestChatConversationFailure,
    ChatConversationActions.createChatConversationFailure,
    (state, { error }): State => ({
      ...state,
      conversation: {
        status: 'failure',
        error,
        isRunning: false,
        contextUsage: 0,
      },
    }),
  ),

  on(
    ChatMessagesActions.loadChatMessagesSuccess,
    ChatPollingActions.chatMessagesPolled,
    (state, { chatMessagesResponse }): State => {
      const boundary = findNewConversationBoundary(state, chatMessagesResponse.messages);
      const newConversationBoundaryMessageIds = boundary
        ? [...state.newConversationBoundaryMessageIds, boundary.id]
        : state.newConversationBoundaryMessageIds;

      return entityAdapter.upsertMany(chatMessagesResponse.messages, {
        ...state,
        conversation: {
          ...state.conversation,
          isRunning: chatMessagesResponse.isRunning,
          contextUsage: chatMessagesResponse.contextUsage ?? 0,
        },
        runningTemplateConfigurationSetup: reportedTemplateConfigurationSetup(
          state,
          chatMessagesResponse.wizardName,
        ),
        hasLoadedMessages: true,
        awaitingNewConversationBoundary: state.awaitingNewConversationBoundary && !boundary,
        newConversationBoundaryMessageIds,
      });
    },
  ),
  on(
    ChatPollingActions.startPollingChatMessages,
    (state, { chatConversationId }): State => ({
      ...state,
      polling: {
        status: 'pending',
        error: undefined,
        chatConversationId,
      },
    }),
  ),
  on(
    ChatPollingActions.chatMessagesPollingFailure,
    (state, { error }): State => ({
      ...state,
      polling: {
        status: 'failure',
        error,
      },
    }),
  ),

  on(
    ChatMessagesActions.sendChatMessage,
    (state, { clientId, message }): State =>
      entityAdapter.addOne(
        {
          id: clientId,
          createdAt: new Date().toISOString(),
          $type: 'user',
          attachments: message.$type === 'text' ? (message.attachments ?? []) : [],
          text: toOptimisticText(message),
        },
        { ...state, sendError: undefined, lastFailedMessage: undefined },
      ),
  ),
  on(
    ChatMessagesActions.sendChatMessageSuccess,
    (state, { chatConversationId, clientId, message }): State => {
      if (!answersAttachedConversation(state, chatConversationId)) {
        return state;
      }

      const withoutOptimisticCopy = entityAdapter.removeOne(clientId, {
        ...state,
        conversation: {
          ...state.conversation,
          isRunning: message.$type === 'user' || state.conversation.isRunning,
        },
      });
      return entityAdapter.upsertOne(message, withoutOptimisticCopy);
    },
  ),
  on(
    ChatMessagesActions.sendChatMessageFailure,
    (state, { chatConversationId, clientId, error, message }): State => {
      if (!putsRightThreadOnScreen(state, chatConversationId)) {
        return state;
      }

      return entityAdapter.removeOne(clientId, {
        ...state,
        sendError: error,
        lastFailedMessage: message,
      });
    },
  ),

  on(
    ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetup,
    (state, { name }): State => ({
      ...state,
      runningTemplateConfigurationSetup: name,
      awaitingTemplateConfigurationSetupOutcome: true,
      conversation: { ...state.conversation, isRunning: true },
    }),
  ),
  on(
    ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetupSuccess,
    ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetupSuccess,
    ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetupFailure,
    (state): State => ({ ...state, awaitingTemplateConfigurationSetupOutcome: false }),
  ),
  on(
    ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetupFailure,
    (state, { chatConversationId }): State => {
      if (!putsRightThreadOnScreen(state, chatConversationId)) {
        return state;
      }

      if (!chatConversationId && !state.runningTemplateConfigurationSetup) {
        return state;
      }

      return {
        ...state,
        runningTemplateConfigurationSetup: undefined,
        awaitingTemplateConfigurationSetupOutcome: false,
        conversation: { ...state.conversation, isRunning: false },
      };
    },
  ),
  on(
    ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetup,
    (state): State => ({
      ...state,
      runningTemplateConfigurationSetup: undefined,
      awaitingTemplateConfigurationSetupOutcome: true,
    }),
  ),

  on(
    ChatActionTargetActions.pendingActionTargetRecorded,
    (state, { target }): State => ({ ...state, pendingActionTarget: target }),
  ),
  on(
    ChatActionTargetActions.pendingActionTargetConsumed,
    (state): State => ({ ...state, pendingActionTarget: undefined }),
  ),

  on(
    ChatFeedbackActions.sendFeedback,
    (state, { feedback, messageId }): State => ({
      ...state,
      feedback: {
        ...state.feedback,
        [messageId]: feedback,
      },
    }),
  ),
  on(ChatFeedbackActions.sendFeedbackFailure, (state, { messageId }): State => {
    const feedback = { ...state.feedback };
    delete feedback[messageId];
    return { ...state, feedback };
  }),

  on(
    ChatInterruptActions.interruptChat,
    (state): State => ({
      ...state,
      interruptError: undefined,
    }),
  ),
  on(ChatInterruptActions.interruptChatSuccess, (state, { chatConversationId }): State => {
    if (!answersAttachedConversation(state, chatConversationId)) {
      return state;
    }

    return {
      ...state,
      conversation: {
        ...state.conversation,
        isRunning: false,
      },
      awaitingNewConversationBoundary: true,
    };
  }),
  on(ChatInterruptActions.interruptChatFailure, (state, { chatConversationId, error }): State => {
    if (!putsRightThreadOnScreen(state, chatConversationId)) {
      return state;
    }

    return { ...state, interruptError: error };
  }),
);
